/**
 * Pure DetailHeaderContact component for use in both NextJS and Electron.
 * Receives all data as props - no hooks, no data fetching.
 */

import type { ReactNode } from 'react';

import Avatar from '../Avatar/Avatar';
import { cn } from '../cn';
import AvatarGroup from './AvatarGroup';

export type DetailHeaderContactPureProps = {
  /** Contact display name */
  name: string;
  /** Contact avatar URL(s) */
  avatarUrls?: (string | null | undefined)[];
  /** LinkedIn profile URL */
  linkedinUrl?: string;
  /** Contact headline or title */
  subtitle?: string;
  /** Organization name */
  orgName?: string;
  /** Organization avatar URL */
  orgAvatarUrl?: string | null;
  /** Click handler for org - if not provided, org is not clickable */
  onClickOrg?: () => void;
  /** Whether to show honorific before name */
  displayHonorific?: boolean;
  /** Honorific title (e.g., "Dr.", "Mr.") */
  honorificTitle?: string;
  /** Parenthetical nickname parsed from the name (e.g., "Mike"), shown muted after the name */
  nickname?: string;
  /** User who owns this contact in the cadence */
  cadenceContactUser?: {
    userId: string;
    profilePicture?: string | null;
    userTeamName?: string | null;
    userEmail: string;
  };
  /** Whether the contact is connected on LinkedIn */
  isConnected?: boolean;
  /** Tooltip label for the user avatar */
  userTooltipLabel?: string;
  /** Additional content to render */
  children?: ReactNode;
};

export default function DetailHeaderContactPure({
  name,
  avatarUrls = [],
  linkedinUrl,
  subtitle,
  orgName,
  orgAvatarUrl,
  onClickOrg,
  displayHonorific,
  honorificTitle,
  nickname,
  cadenceContactUser,
  isConnected,
  userTooltipLabel,
  children,
}: DetailHeaderContactPureProps) {
  const hasValidLinkedInUrl = linkedinUrl != null;

  return (
    <div className="flex min-w-0 shrink grow gap-2">
      {/* Avatar with LinkedIn link */}
      <div className="flex flex-col items-center gap-1">
        {!hasValidLinkedInUrl && (
          <span className="relative self-start no-underline" title="LinkedIn profile not available">
            <AvatarGroup
              avatarUrls={avatarUrls}
              avatarAlt={name || '?'}
              cadenceContactUser={cadenceContactUser}
              isConnected={isConnected}
              userTooltipLabel={userTooltipLabel}
            />
          </span>
        )}
        {hasValidLinkedInUrl && (
          <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="relative self-start no-underline">
            <AvatarGroup
              avatarUrls={avatarUrls}
              avatarAlt={name || '?'}
              cadenceContactUser={cadenceContactUser}
              isConnected={isConnected}
              userTooltipLabel={userTooltipLabel}
            />
          </a>
        )}
      </div>

      {/* Contact info */}
      <div className="flex w-0 min-w-0 shrink grow basis-0 flex-col gap-1">
        <div
          className="max-h-[30px] min-h-[30px] overflow-hidden text-ellipsis text-xl font-semibold leading-[30px] tracking-tight text-slate-950 dark:text-slate-50"
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 1,
          }}>
          {displayHonorific && honorificTitle && (
            <span className="text-gray-400 dark:text-gray-500">{honorificTitle} </span>
          )}
          {name}
          {nickname && <span className="text-gray-400 dark:text-gray-500"> ({nickname})</span>}
        </div>
        {subtitle && (
          <div
            className="h-5 overflow-hidden text-ellipsis text-sm leading-5 text-slate-500 dark:text-slate-400"
            style={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
            }}>
            {subtitle}
          </div>
        )}
        {orgName && (
          <div
            className={cn(
              'flex h-6 flex-row gap-1.5 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold leading-[160%] tracking-tight text-slate-600 dark:text-slate-300',
              onClickOrg && 'cursor-pointer hover:underline',
            )}
            onClick={onClickOrg}>
            <Avatar srcs={orgAvatarUrl ? [orgAvatarUrl] : []} alt={orgName} shape="square" className="size-5.5" />
            <span className="overflow-hidden text-ellipsis">{orgName}</span>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export function DetailHeaderContactLoading() {
  return (
    <div className="flex min-w-0 shrink grow gap-2">
      <div className="size-10 animate-pulse rounded-full bg-astronaut-200 dark:bg-astronaut-700" />
      <div className="flex w-0 shrink grow basis-0 flex-col gap-1">
        <div className="h-[30px] w-32 animate-pulse rounded-sm bg-astronaut-200 dark:bg-astronaut-700" />
        <div className="h-5 w-48 animate-pulse rounded-sm bg-astronaut-200 opacity-60 dark:bg-astronaut-700" />
        <div className="h-6 w-36 animate-pulse rounded-sm bg-astronaut-200 opacity-20 dark:bg-astronaut-700" />
      </div>
    </div>
  );
}
